import {useEffect} from "react";
import {createPortal} from "react-dom";
import classNames from "classnames";

const AUTOFOCUS_SELECTOR = [
    'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]):not([disabled]):not([readonly])',
    'textarea:not([disabled]):not([readonly])',
    'select:not([disabled])',
    '[contenteditable="true"]'
].join(', ');

export default function BrickConfiguratorPopover({
    open,
    panelRef,
    panelStyle,
    title,
    description,
    body,
    submitLabel,
    submitDisabled,
    onCancel,
    onSubmit
}) {
    useEffect(() => {
        if (!open) return;

        const panel = panelRef?.current;
        const visible = (panelStyle?.visibility || 'hidden') !== 'hidden';
        if (!panel || !visible) return;

        let focusFrame = requestAnimationFrame(() => {
            focusFrame = requestAnimationFrame(() => {
                if (document.activeElement && panel.contains(document.activeElement)) {
                    return;
                }

                const nextField = panel.querySelector(AUTOFOCUS_SELECTOR);
                nextField?.focus({preventScroll: true});
            });
        });

        return () => cancelAnimationFrame(focusFrame);
    }, [open, panelRef, panelStyle?.left, panelStyle?.top, panelStyle?.visibility, panelStyle?.width]);

    if (!open) return null;

    return createPortal(
        <div
            ref={panelRef}
            style={panelStyle || {position: 'fixed', left: '-9999px', top: '-9999px', width: '352px', zIndex: 1200, visibility: 'hidden'}}
            className="flex flex-col rounded-xl border border-gray-200 bg-white p-3 shadow-xl"
        >
            <div className="mb-3 shrink-0">
                <div className="text-sm font-semibold text-gray-900">{title || 'Brick'}</div>
                {description ? (
                    <div className="text-xs leading-5 text-gray-500">{description}</div>
                ) : null}
            </div>
            <div className="min-h-0 overflow-y-auto">{body}</div>
            <div className="mt-3 flex shrink-0 justify-end gap-3 border-t border-gray-200 pt-3">
                <button
                    type="button"
                    className="text-sm font-medium text-gray-600 hover:text-gray-800"
                    onClick={onCancel}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className={classNames(
                        "text-sm font-semibold text-primary-600 hover:text-primary-700",
                        submitDisabled ? "cursor-not-allowed opacity-50" : null
                    )}
                    disabled={submitDisabled}
                    onClick={onSubmit}
                >
                    {submitLabel}
                </button>
            </div>
        </div>,
        document.body
    );
}
